/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback, useContext, useMemo } from 'react';
import ClockIcon from '@atlaskit/icon/core/clock';
import {
	FlyoutMenuItem,
	FlyoutMenuItemTrigger,
	FlyoutMenuItemContent,
} from '@atlaskit/side-nav-items/flyout-menu-item';
import { useEntryPointButtonTrigger } from '@jira/platform__entry-point-button-trigger/src/index.tsx';
import { JiraEntryPointContainer } from '@jira/platform__entry-points-container/src/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import { useEntryPoint } from '@jira/platform__entry-point/src/controllers/use-entry-point/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { NAVIGATION_ITEM } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/action-subject-type.tsx';
import { SECTION_ITEM_RECENT } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { NavItemsContext } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/controllers/context.tsx';
import { L1_MENU_ID } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { SkeletonNavMenuItems } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/skeleton-nav-menu-items/main.tsx';
import { useFormattedMessageAndIcon } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-formatted-message-and-icon/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { PACKAGE_NAME, TEAM_NAME, RECENT_QUERY_LIMIT } from '../common/constants.tsx';
import { sidebarRecentMenuEntryPoint } from './recent-menu/sidebarRecentMenuEntryPoint.entrypoint.tsx';

/* eslint-disable jira/i18n/no-export-define-message */
/* eslint-disable jira/filename-case */
// eslint-disable-next-line no-restricted-imports
import { defineMessage } from '@atlassian/jira-intl';

const recent = defineMessage({
	id: 'navigation-apps-sidebar-nav4-recent.recent',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Recent',
	description: 'Label for the recent nav menu item in the sidebar',
});

const EMPTY_OBJECT = {};

export function RecentMenu(): React.JSX.Element {
	const cloudId = useCloudId();
	const { formattedMessage, icon } = useFormattedMessageAndIcon(recent, ClockIcon);
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		sidebarRecentMenuEntryPoint,
		useMemo(() => ({ cloudId, count: RECENT_QUERY_LIMIT }), [cloudId]),
	);

	const triggerRef = useEntryPointButtonTrigger(entryPointActions);
	const { moreMenuIds } = useContext(NavItemsContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onOpenChange = useCallback(
		(isMenuOpen: boolean) => {
			if (isMenuOpen) {
				const event = createAnalyticsEvent({
					action: 'clicked',
					actionSubject: NAVIGATION_ITEM,
				});
				const isInMore = moreMenuIds.includes(L1_MENU_ID.RECENT);
				fireUIAnalytics(event, SECTION_ITEM_RECENT, { isInMore });
			}
		},
		[moreMenuIds, createAnalyticsEvent],
	);
	return (
		<FlyoutMenuItem onOpenChange={onOpenChange}>
			<FlyoutMenuItemTrigger
				interactionName="nav4-sidebar-recent"
				ref={triggerRef}
				elemBefore={icon}
			>
				{formattedMessage}
			</FlyoutMenuItemTrigger>
			<FlyoutMenuItemContent
				maxHeight={fg('platform_dst_nav4_flyout_menu_slots_close_button') ? 460 : undefined}
			>
				<JiraEntryPointContainer
					entryPointReferenceSubject={entryPointReferenceSubject}
					id="nav4-sidebar-recent-default"
					packageName={PACKAGE_NAME}
					teamName={TEAM_NAME}
					errorFallback="flag"
					fallback={
						<SkeletonNavMenuItems withHeading withSearch withSubHeading withSubTitle isFlyout />
					}
					runtimeProps={EMPTY_OBJECT}
				/>
			</FlyoutMenuItemContent>
		</FlyoutMenuItem>
	);
}
